import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

interface MetricData {
    date: string;
    active_users?: number;
    tasks_created?: number;
    tasks_completed?: number;
    daily_revenue?: number;
}

interface MetricTrendChartProps {
    title: string;
    description?: string;
    metricType?: string;
}

export function MetricTrendChart({ title, description, metricType }: MetricTrendChartProps) {
    const { session } = useAuth();
    const [data, setData] = useState<MetricData[]>([]);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState("30");

    useEffect(() => {
        loadMetrics();
    }, [days, metricType]);

    const loadMetrics = async () => {
        setLoading(true);
        try {
            const { data: result, error } = await supabase.functions.invoke("admin-actions", {
                body: {
                    action: "get_metrics_trend",
                    metricType,
                    days: parseInt(days)
                },
                headers: { Authorization: `Bearer ${session?.access_token}` },
            });

            if (error) throw error;

            // Transform grouped data for chart
            const chartData: MetricData[] = Object.entries(result.grouped || {}).map(([date, metrics]: [string, any]) => ({
                date: new Date(date).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }),
                ...metrics,
            }));

            setData(chartData);
        } catch (error) {
            console.error("Error loading metrics:", error);
        } finally {
            setLoading(false);
        }
    };

    const getTrend = () => {
        if (data.length < 2) return { direction: "neutral", percentage: 0 };

        const recent = data.slice(-7);
        const previous = data.slice(-14, -7);

        if (recent.length === 0 || previous.length === 0) return { direction: "neutral", percentage: 0 };

        const recentSum = recent.reduce((sum, d) => sum + (d.active_users || d.tasks_created || 0), 0);
        const previousSum = previous.reduce((sum, d) => sum + (d.active_users || d.tasks_created || 0), 0);

        if (previousSum === 0) return { direction: "neutral", percentage: 0 };

        const change = ((recentSum - previousSum) / previousSum) * 100;

        return {
            direction: change > 5 ? "up" : change < -5 ? "down" : "neutral",
            percentage: Math.abs(Math.round(change)),
        };
    };

    const trend = getTrend();

    return (
        <Card className="col-span-full">
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                    <CardTitle className="flex items-center gap-2">
                        {title}
                        {trend.direction === "up" && (
                            <span className="flex items-center text-sm text-green-600 dark:text-green-400">
                                <TrendingUp className="w-4 h-4 mr-1" />
                                +{trend.percentage}%
                            </span>
                        )}
                        {trend.direction === "down" && (
                            <span className="flex items-center text-sm text-red-600 dark:text-red-400">
                                <TrendingDown className="w-4 h-4 mr-1" />
                                -{trend.percentage}%
                            </span>
                        )}
                        {trend.direction === "neutral" && (
                            <span className="flex items-center text-sm text-muted-foreground">
                                <Minus className="w-4 h-4 mr-1" />
                                Estável
                            </span>
                        )}
                    </CardTitle>
                    {description && <CardDescription>{description}</CardDescription>}
                </div>
                <Select value={days} onValueChange={setDays}>
                    <SelectTrigger className="w-32">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="7">7 dias</SelectItem>
                        <SelectItem value="14">14 dias</SelectItem>
                        <SelectItem value="30">30 dias</SelectItem>
                        <SelectItem value="90">90 dias</SelectItem>
                    </SelectContent>
                </Select>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <Skeleton className="h-64 w-full" />
                ) : data.length === 0 ? (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                        Nenhuma métrica disponível ainda. Os dados serão coletados automaticamente.
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="colorTasks" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 12 }}
                                tickLine={false}
                                axisLine={false}
                            />
                            <YAxis
                                tick={{ fontSize: 12 }}
                                tickLine={false}
                                axisLine={false}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: "hsl(var(--card))",
                                    border: "1px solid hsl(var(--border))",
                                    borderRadius: "8px",
                                }}
                            />
                            {!metricType && (
                                <>
                                    <Area
                                        type="monotone"
                                        dataKey="active_users"
                                        name="Usuários Ativos"
                                        stroke="hsl(var(--primary))"
                                        fill="url(#colorUsers)"
                                        strokeWidth={2}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="tasks_created"
                                        name="Tasks Criadas"
                                        stroke="hsl(142, 76%, 36%)"
                                        fill="url(#colorTasks)"
                                        strokeWidth={2}
                                    />
                                </>
                            )}
                            {metricType && (
                                <Area
                                    type="monotone"
                                    dataKey={metricType}
                                    name={title}
                                    stroke="hsl(var(--primary))"
                                    fill="url(#colorUsers)"
                                    strokeWidth={2}
                                />
                            )}
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
}
